import {
  Box,
  Checkbox,
  Flex,
  Td,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { CustomLinkOverlay } from 'BrokerTsLib/src/components'
import type { InsuranceParamsGridItem, InsuranceParamsVehicleGridItem } from 'BrokerTsLib/src/dtos'
import { formatPersonFullName, formatVehicle } from 'BrokerTsLib/src/dtos'
import type { IOfferCompany } from 'BrokerTsLib/src/enums'
import { Product } from 'BrokerTsLib/src/enums'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'

import { InsuranceParamsTypeBadge } from '@/components/InsuranceParamsTypeBadge'
import { VehicleLogo } from '@/components/VehicleLogo'
import { useGetInsuranceParamsNavigationTarget } from '@/hooks/useInsuranceParams'
import { useGetInsuranceParamsTypes } from '@/hooks/useInsuranceParamsTypes'
import { Note } from '@/icons'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { CompanyLogo } from '../../components/CompanyLogo'
import { TableData, TableRow } from '../../components/Grid'
import { StepsProgress } from '../../components/StepsProgress'
import { useApp } from '../../hooks/useApp'

export const logoSize = 44

interface ConceptRowProps {
  allowSelect: boolean
  selectedRows: InsuranceParamsVehicleGridItem['insuranceParamsId'][]
  onRowSelect: (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => void
}

export const ConceptRow = ({ allowSelect, onRowSelect, selectedRows, item }: { item: InsuranceParamsGridItem } & ConceptRowProps) => {
  const { product } = useApp()
  const { translate } = useTranslationsContext()
  const { insuranceParamsId: id } = item
  const isSelected = selectedRows.includes(id)

  let vehicleItem: InsuranceParamsVehicleGridItem | null = null
  if ([Product.VehicleInsurance, Product.VehicleLeasingInsurance].includes(product!))
    vehicleItem = item as InsuranceParamsVehicleGridItem

  let insurer: IOfferCompany | undefined
  if (item.insurerId)
    insurer = { id: item.insurerId, name: item.insurerFullName! }
  const insureeName = formatPersonFullName(product == Product.VehicleLeasingInsurance ? item.client : item.policyHolder)
  const subject = formatVehicle(vehicleItem?.vehicle)
  const brokerFullName = `${item.createdBy?.name ?? ''} ${item.createdBy?.surname ?? ''}`.trim()

  const showLogo = [Product.VehicleInsurance, Product.VehicleLeasingInsurance].includes(product!)

  const navigateTarget = useGetInsuranceParamsNavigationTarget(item)
  const insuranceParamsTypes = useGetInsuranceParamsTypes()

  return (
    <CustomLinkOverlay as={TableRow} to={navigateTarget} textDecoration={item.deleted ? 'line-through' : 'none'} pointerEvents={item.deleted ? 'none' : 'initial'}>
      <Td
        display={allowSelect ? 'table-cell' : 'none'}
        onClick={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        w='60px'
      >
        <Checkbox
          gap={0}
          isChecked={isSelected}
          onChange={(e) => onRowSelect(id)}
        />
      </Td>

      <TableData>
        <Flex sx={{ columnGap: 4, rowGap: 1, flexWrap: 'wrap', alignItems: 'center' }}>

          {insuranceParamsTypes.length > 1 &&
            <InsuranceParamsTypeBadge type={item.type} offerType={item.offerType} />
          }
          <Box sx={{ display: 'grid', gap: 1 }} className="contract__id-progress">
            <Text sx={{ fontWeight: 'bold', fontSize: 'md' }}>{id}</Text>

            <StepsProgress progress={(item.step ?? 1)} ml={1} />
          </Box>

          {item.note && (
            <Tooltip label={item.note} hasArrow placement="top" shouldWrapChildren>
              <Note color="secondary.500" />
            </Tooltip>
          )}
        </Flex>
      </TableData>

      <TableData>
        {item.dateCreated && <Text sx={{ fontSize: 'sm' }}>
          {formatDate(item.dateCreated, 'd.M.yyyy')}
        </Text>
        }

        {item.startDate && (
          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>
            {formatDate(item.startDate, 'd.M.yyyy')}
          </Text>
        )}
      </TableData>

      <TableData sx={{ px: 0 }}>
        {showLogo && <VehicleLogo druh={vehicleItem?.vehicle?.druh} znacka={vehicleItem?.vehicle?.znacka} mx='auto' />}
      </TableData>


      <TableData sx={{ pl: 0 }}>
        {insureeName && <Text sx={{ fontSize: 'sm' }}>{insureeName}</Text>}


        {!!subject &&
          <Text
            sx={{
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subject}
          </Text>
        }
      </TableData>

      <TableData>
        {insurer && <CompanyLogo
          insurer={insurer}
          width="64px"
          maxH="48px"
          offerType={item.offerType}
        />
        }
      </TableData>

      <TableData>
        {item.name && (
          <Text sx={{ color: 'gray.600', fontSize: 'sm' }} className="contract__name">
            {item.name}
          </Text>
        )}
        {!item.name && (
          <Text sx={{ color: 'gray.400', fontSize: 'xs' }} className="contract__name">{translate('noName', 'Nezadáno')}</Text>
        )}
        {brokerFullName && (
          <Text sx={{ color: 'primary.500', fontSize: 'sm', fontWeight: 'semibold' }}>
            {brokerFullName}
          </Text>
        )}
      </TableData>
    </CustomLinkOverlay>
  )
}
